// Native media and file elements Tamagui has no component for: a camera video, a drawing canvas, a
// picture that can fall back, a file picker and a range slider. Screens use these, never the tags.
import "./config";
import { forwardRef } from "react";

const HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
};

/** A live camera feed filling its container; assistive technology skips it, the controls speak. */
export const Video = forwardRef<HTMLVideoElement, { maxHeight?: string }>(function Video(
  { maxHeight },
  ref,
) {
  return (
    <video
      ref={ref}
      aria-hidden="true"
      muted
      playsInline
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        background: "var(--bg2)",
        ...(maxHeight === undefined ? {} : { maxHeight }),
      }}
    />
  );
});

export interface CanvasProps {
  /** The drawing's own resolution, in pixels. */
  width: number;
  height: number;
  /** The size it is shown at, in CSS pixels; left out, the canvas shows at its resolution. */
  displayWidth?: number;
  /** Off screen for everyone: a scratch surface a frame is read from. */
  hidden?: boolean;
  /** Names a visible drawing (a QR code); a hidden or decorative canvas has none. */
  label?: string;
}

/** A canvas: hidden as a scratch surface, or shown as a drawing. */
export const Canvas = forwardRef<HTMLCanvasElement, CanvasProps>(function Canvas(
  { width, height, displayWidth, hidden = false, label },
  ref,
) {
  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(label === undefined || hidden
        ? { "aria-hidden": true }
        : { role: "img", "aria-label": label })}
      style={{
        ...(hidden ? HIDDEN : {}),
        ...(displayWidth === undefined
          ? {}
          : { width: displayWidth, height: (displayWidth * height) / width }),
      }}
    />
  );
});

export interface ImgProps {
  src: string;
  /** What the picture shows; `""` marks it decorative. */
  alt: string;
  width?: number | string;
  height?: number | string;
  fit?: "cover" | "contain";
  /** The picture failed to load; swap `image.src` for another source if there is one. */
  onFailure?: (image: HTMLImageElement) => void;
}

/** A picture that can recover from a failed load (a catalogue label the edge can serve instead). */
export function Img({
  src,
  alt,
  width,
  height,
  fit = "contain",
  onFailure,
}: ImgProps): React.JSX.Element {
  return (
    <img
      src={src}
      alt={alt}
      style={{
        display: "block",
        maxWidth: "100%",
        objectFit: fit,
        ...(width === undefined ? {} : { width }),
        ...(height === undefined ? {} : { height }),
      }}
      onError={(event) => {
        onFailure?.(event.currentTarget);
      }}
    />
  );
}

export interface HiddenFileInputProps {
  accept: string;
  /** The camera to open on a phone (`environment` is the rear one). */
  capture?: "user" | "environment";
  /** The chosen file; the picker is cleared so the same file can be chosen again. */
  onFile: (file: File | undefined) => void;
  testId?: string;
  label?: string;
}

/** A file picker nobody sees: a Button calls `ref.current?.click()` to open it. */
export const HiddenFileInput = forwardRef<HTMLInputElement, HiddenFileInputProps>(
  function HiddenFileInput({ accept, capture, onFile, testId, label }, ref) {
    return (
      <input
        ref={ref}
        type="file"
        accept={accept}
        // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
        {...(capture === undefined ? {} : { capture })}
        // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
        {...(label === undefined ? { "aria-hidden": true, tabIndex: -1 } : { "aria-label": label })}
        // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
        {...(testId === undefined ? {} : { "data-testid": testId })}
        style={HIDDEN}
        onChange={(event) => {
          onFile(event.currentTarget.files?.[0]);
          event.currentTarget.value = "";
        }}
      />
    );
  },
);

export interface RangeProps {
  /** The slider's accessible name. */
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onValueChange: (value: number) => void;
}

/** A native slider in the accent colour; the platform's own control is the most usable one. */
export function Range({
  label,
  min,
  max,
  step,
  value,
  onValueChange,
}: RangeProps): React.JSX.Element {
  return (
    <input
      type="range"
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={value}
      style={{ flex: 1, minWidth: 0, minHeight: "var(--uiTarget)", accentColor: "var(--accent)" }}
      onChange={(event) => {
        onValueChange(event.currentTarget.valueAsNumber);
      }}
    />
  );
}
